import { useQueries, useQuery } from '@tanstack/react-query'
import { onLine, PrintRows, PrintRunning, type Rec, resourceApi, SignatureMark, useCompany } from '@tnn/app-kit'
import { bahtText, formatDate, formatMoney, formatPhone, formatTaxId, labels, type RefMap } from '@tnn/core'
import { type Allocation, invoice, type MoneyIn, payment } from '@tnn/domain'
import { useTranslation } from 'react-i18next'
import classes from './document.module.css'
import { CustomerAddress, CustomerTaxLine, LinesClose, useIssuedSignatures } from './InvoiceDocument'

/**
 * A4 receipt for money a customer paid (ใบรับเงิน), or for a deposit (ใบรับเงินมัดจำ): what it
 * settles — invoices, a charter job — then the money as it came in, withholding tax deducted,
 * and the amount received in words. Same page layout as the invoice.
 */
export function PaymentDocument({ record, refs, copy = false }: { record: Rec; refs: RefMap; copy?: boolean }) {
  const { t } = useTranslation()
  const company = useCompany().data
  const signed = useIssuedSignatures('accounting.payment', record)
  const customer = useQuery({
    queryKey: ['r', 'accounting.customer', 'record', String(record.customerId)],
    queryFn: () => resourceApi('accounting.customer').get(String(record.customerId)),
    enabled: !!record.customerId,
  }).data?.item
  const allocations = (record.allocations ?? []) as Allocation[]
  const invoices = useQueries({
    queries: allocations.map((a) => ({
      queryKey: ['r', invoice.key, 'record', a.invoiceId],
      queryFn: () => resourceApi(invoice.key).get(a.invoiceId),
    })),
  })
  const money = (record.payments ?? []) as MoneyIn[]
  const deposit = record.kind === 'deposit'
  const received = Number(record.received ?? 0)
  const unallocated = Number(record.unallocated ?? 0)
  const branch = (b?: string | null) =>
    !b || b === '00000' ? t('accounting:customer.headOffice') : `${t('accounting:customer.fields.branch')} ${b}`
  const p = (k: string) => t(`accounting:payment.print.${k}`)
  const f = (k: string) => t(`accounting:payment.fields.${k}`)
  const job = record.shipmentId ? (refs['tms.shipment']?.[String(record.shipmentId)] ?? '') : ''
  // What the money is for: each invoice settled, then what is kept (a deposit or an advance)
  const lines = [
    ...allocations.map((a, i) => ({
      key: a.invoiceId,
      text: `${p('forInvoices')} ${t('accounting:invoice.title')} ${String(invoices[i]?.data?.item.number ?? '')}`,
      amount: a.amount,
    })),
    ...(unallocated > 0 ? [{ key: 'kept', text: deposit ? `${p('forJob')} ${job}`.trim() : f('unallocated'), amount: unallocated }] : []),
  ]

  const docName = t(deposit ? 'accounting:payment.depositTitle' : 'accounting:payment.documentTitle')
  const receivedOn = formatDate(String(record.receivedOn), { style: 'long' })
  const copyLabel = t(copy ? 'accounting:invoice.print.copy' : 'accounting:invoice.print.original')

  return (
    <article className={classes.doc}>
      <PrintRunning start={[company?.name, docName]} end={[String(record.number ?? ''), receivedOn, copyLabel]} />
      <header className={classes.head}>
        <div className={classes.from}>
          <div className={classes.company}>{company?.name}</div>
          <div>{company?.address}</div>
          <div>
            {company?.taxId && `${t('accounting:customer.fields.taxId')} ${formatTaxId(company.taxId)} · ${branch(company.branch)}`}
          </div>
          <div>{[formatPhone(company?.phone), company?.email].filter(Boolean).join(' · ')}</div>
        </div>
        <div className={classes.title}>
          <div className={classes.docName}>{docName}</div>
          <div className={classes.copy}>{copyLabel}</div>
        </div>
      </header>

      <section className={classes.parties}>
        <div>
          <div className={classes.caption}>{p('receivedFrom')}</div>
          <div className={classes.strong}>{String(customer?.name ?? refs['accounting.customer']?.[String(record.customerId)] ?? '')}</div>
          <CustomerAddress customer={customer} />
          <CustomerTaxLine customer={customer} />
        </div>
        <dl className={classes.meta}>
          <dt>{f('number')}</dt>
          <dd className={classes.strong}>{String(record.number ?? '')}</dd>
          <dt>{f('receivedOn')}</dt>
          <dd>{receivedOn}</dd>
        </dl>
      </section>

      <table className={classes.lines}>
        <thead>
          <tr>
            <th style={{ width: '6%' }}>#</th>
            <th>{t('accounting:invoice.line.description')}</th>
            <th data-num style={{ width: '20%' }}>
              {t('accounting:invoice.line.amount')}
            </th>
          </tr>
        </thead>
        <PrintRows
          rows={lines.map((line, i) => (
            <tr key={line.key}>
              <td>{i + 1}</td>
              <td>{line.text}</td>
              <td data-num>{formatMoney(line.amount)}</td>
            </tr>
          ))}
          close={
            <LinesClose columns={3}>
              <section className={classes.summary}>
                <div className={classes.words}>
                  <div className={classes.caption}>{t('accounting:invoice.totals.inWords')}</div>
                  <div className={classes.strong}>({bahtText(received)})</div>
                  {/* How the money came in: transfers with their reference, cheques with their number */}
                  {money.map((m, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: printed lines are positional
                    <div key={i}>
                      {t(labels.option(payment, 'method', m.method))} {formatMoney(m.amount)}
                      {m.reference ? ` · ${m.reference}` : ''}
                    </div>
                  ))}
                  {record.whtCertNo ? (
                    <div>
                      {f('whtCertNo')} {String(record.whtCertNo)}
                    </div>
                  ) : null}
                  {record.notes ? <p className={classes.notes}>{String(record.notes)}</p> : null}
                </div>
                <dl className={classes.totals}>
                  <dt>{f('settled')}</dt>
                  <dd>{formatMoney(Number(record.settled ?? 0))}</dd>
                  {Number(record.whtAmount ?? 0) > 0 && (
                    <>
                      <dt>{f('whtAmount')}</dt>
                      <dd>-{formatMoney(Number(record.whtAmount))}</dd>
                    </>
                  )}
                  {Number(record.writeOff ?? 0) > 0 && (
                    <>
                      <dt>{f('writeOff')}</dt>
                      <dd>-{formatMoney(Number(record.writeOff))}</dd>
                    </>
                  )}
                  <dt className={classes.grand}>{f('received')}</dt>
                  <dd className={classes.grand}>{formatMoney(received)}</dd>
                </dl>
              </section>

              <footer className={classes.signs}>
                <SignatureMark label={p('payer')} signature={onLine(signed, 'customer')} />
                <SignatureMark label={p('receiver')} signature={onLine(signed, 'receiver')} />
              </footer>
            </LinesClose>
          }
        />
      </table>
    </article>
  )
}
